import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';

const FavoritesScreen = () => {
  const [recipes, setRecipes]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [user]   = useAuthState(auth);

  useEffect(() => {
    const fetch = async () => {
      if (!user) { setLoading(false); return; }
      setLoading(true);
      try {
        const q    = query(collection(db, 'recipes'), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        setRecipes(snap.docs.map(d => ({ ...d.data(), id: d.id })));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [user]);

  const favorites = [...recipes]
    .filter(r => r.isFavorite === true)
    .filter(r => r.content?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return 0;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-on-surface-variant font-medium">טוען מועדפים...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-on-surface font-body pb-32" dir="rtl">

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-[#f9f9f9]/80 backdrop-blur-md px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-surface-container transition-colors active:scale-95">
          <span className="material-symbols-outlined text-primary">arrow_forward</span>
        </button>
        <h1 className="font-headline font-bold text-xl text-primary">המועדפים שלי ❤️</h1>
      </header>

      <main className="max-w-2xl mx-auto px-6 pt-6">

        {/* ── Title ── */}
        <div className="mb-8">
          <p className="text-primary font-bold tracking-widest text-[11px] uppercase mb-1">YOUR COLLECTION</p>
          <h2 className="text-3xl font-headline font-extrabold tracking-tight mb-1">המתכונים שהכי אהבת</h2>
          <p className="text-on-surface-variant text-sm">{favorites.length} מתכונים מועדפים</p>
        </div>

        {/* ── Search ── */}
        <div className="relative mb-8">
          <input
            type="text"
            placeholder="חיפוש במועדפים..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full py-4 pr-12 pl-6 bg-surface-container-lowest border-none rounded-full recipe-card-shadow text-on-surface placeholder:text-outline focus:ring-4 focus:ring-primary/15 transition-all outline-none"
          />
          <span className="material-symbols-outlined absolute right-5 top-1/2 -translate-y-1/2 text-primary">search</span>
        </div>

        {/* ── List ── */}
        {favorites.length === 0 ? (
          <div className="py-16 flex flex-col items-center text-center border-2 border-dashed border-outline-variant/30 rounded-xl">
            <span className="material-symbols-outlined text-5xl text-outline-variant mb-4">favorite_border</span>
            <p className="text-on-surface-variant font-medium mb-1">אין לך עדיין מתכונים מועדפים</p>
            <p className="text-sm text-outline">סמן בלב מתכונים מספר המתכונים שלך</p>
          </div>
        ) : (
          <div className="space-y-4">
            {favorites.map(recipe => {
              const title = recipe.content?.split('\n')[0]?.replace('#', '').trim() || 'מתכון';
              return (
                <div
                  key={recipe.id}
                  onClick={() => navigate('/detail', { state: { recipe } })}
                  className="bg-surface-container-lowest p-4 rounded-xl recipe-card-shadow flex items-center gap-4 cursor-pointer border border-transparent hover:border-primary/10 transition-all hover:-translate-y-0.5"
                >
                  {/* thumbnail */}
                  <div className="w-20 h-20 rounded-xl flex-shrink-0 bg-surface-container overflow-hidden">
                    {recipe.imageUrl
                      ? <img src={recipe.imageUrl} alt={title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><span className="material-symbols-outlined text-outline text-2xl">restaurant</span></div>
                    }
                  </div>

                  {/* info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-headline font-bold text-on-surface text-base truncate mb-1">
                      {recipe.isPinned && '📌 '}
                      {title.substring(0, 35)}{title.length > 35 ? '...' : ''}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                        {recipe.category}
                      </span>
                      {recipe.rating > 0 && (
                        <span className="text-xs text-yellow-500 font-bold">★ {recipe.rating}</span>
                      )}
                      <span className="text-xs text-outline">{recipe.date}</span>
                    </div>
                  </div>

                  {/* action buttons */}
                  {/* <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={e => { e.stopPropagation(); navigate('/detail', { state: { recipe } }); }}
                      className="bg-gradient-to-r from-primary to-primary-container text-white px-4 py-2 rounded-full font-label font-bold text-sm hover:opacity-90 active:scale-95 transition-all"
                    >
                      בשל
                    </button>
                  </div> */}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default FavoritesScreen;
